import React from 'react';

import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { INutritionInfo } from '@rbi-ctg/menu';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';

import { Table, TableData, TableRow, TableTitle } from '../styled/table';

import { SanityNutritionItemGroup } from './types';

const ListItem = styled(TableRow)`
  text-indent: 1rem;
`;

const ListItemData = styled(TableData)``;

type NutritionItemGroupProps = {
  nutritionInfo: INutritionInfo;
  nutritionItemGroups: { items: SanityNutritionItemGroup[] }[];
  roundNutritionValue: (val: number) => number;
};

type FormatNutritionValueParams = {
  nutritionValue: number;
  omitFlag: boolean | undefined | null;
  nutrtitionUnit: string;
  enableCommaSeparatorFlag: boolean;
};

export const NutritionItemGroup: React.FC<NutritionItemGroupProps> = ({
  nutritionInfo,
  nutritionItemGroups,
  roundNutritionValue,
}) => {
  const { formatNumber } = useIntl();
  const enableFormatNutritionCommaSeparator = useFlag(
    LaunchDarklyFlag.ENABLE_FORMAT_NUTRITION_COMMA_SEPARATOR
  );

  const formatNutritionValue = ({
    nutritionValue,
    omitFlag,
    nutrtitionUnit,
    enableCommaSeparatorFlag,
  }: FormatNutritionValueParams): string => {
    const formattedValue = enableCommaSeparatorFlag
      ? formatNumber(nutritionValue, { maximumFractionDigits: 1 })
      : nutritionValue.toString();

    return omitFlag ? formattedValue : `${formattedValue}${nutrtitionUnit}`;
  };

  const nutritionItemGroupData = nutritionItemGroups?.[0];

  return (
    <div data-testid="nutrition-item-group">
      {nutritionItemGroupData?.items?.map(groupItem => {
        const mainItemValue =
          nutritionInfo[groupItem?.mainItem?.nutritionKey as keyof INutritionInfo] ?? 0;

        const roundedMainItemValue = roundNutritionValue(mainItemValue);

        const mainItemOmitUnitFromValue = groupItem?.mainItem?.omitUnitFromValue;
        const mainItemUnit = groupItem?.mainItem?.unit?.locale ?? '';

        const getFormattedMainItemNutritionValue = (): string =>
          formatNutritionValue({
            nutritionValue: roundedMainItemValue,
            omitFlag: mainItemOmitUnitFromValue,
            nutrtitionUnit: mainItemUnit,
            enableCommaSeparatorFlag: enableFormatNutritionCommaSeparator,
          });

        return (
          <Table key={groupItem?._key}>
            <TableRow>
              <TableTitle>{groupItem?.mainItem?.name?.locale}</TableTitle>
            </TableRow>
            <TableRow data-testid="nutrition-item-group-main-item">
              <TableData>{groupItem?.mainItem?.name?.locale}</TableData>
              <TableData data-testid="nutrition-item-group-main-item-value">
                {getFormattedMainItemNutritionValue()}
              </TableData>
            </TableRow>

            {groupItem?.lineItems?.map(listItem => {
              const listItemValue =
                nutritionInfo[listItem?.nutritionKey as keyof INutritionInfo] ?? 0;

              const listItemOmitUnitFromValue = listItem?.omitUnitFromValue;
              const listItemUnit = listItem?.unit?.locale ?? '';
              const roundedListItemValue = roundNutritionValue(listItemValue);

              const getFormattedListItemNutritionValue = (): string =>
                formatNutritionValue({
                  nutritionValue: roundedListItemValue,
                  omitFlag: listItemOmitUnitFromValue,
                  nutrtitionUnit: listItemUnit,
                  enableCommaSeparatorFlag: enableFormatNutritionCommaSeparator,
                });

              return (
                <ListItem key={listItem?.nutritionKey} data-testid="nutrition-item-group-line-item">
                  <ListItemData>{listItem?.name?.locale}</ListItemData>
                  <ListItemData data-testid="nutrition-item-group-line-item-value">
                    <span>{getFormattedListItemNutritionValue()}</span>
                  </ListItemData>
                </ListItem>
              );
            })}
          </Table>
        );
      })}
    </div>
  );
};
